/**
 * Indiana State-Specific Diagnostic Questions
 * 
 * DUAL LICENSE STRUCTURE - Indiana has TWO distinct license types:
 * 
 * 1. HOME HEALTH AGENCY (HHA) - Skilled Services
 *    - Governing Law: IC 16-27-1, 410 IAC 17
 *    - Services: Nursing, PT, OT, speech therapy, medical social work, home health aide
 *    - License Fee: $250/year
 *    - Regulatory Body: Indiana Department of Health (IDOH)
 * 
 * 2. PERSONAL SERVICES AGENCY (PSA) - Non-Medical Services
 *    - Governing Law: IC 16-27-4
 *    - Services: Attendant care, homemaker, companion services
 *    - License Fee: $250/year
 *    - Regulatory Body: Indiana Department of Health (IDOH)
 * 
 * SHARED REQUIREMENTS (Both License Types):
 *    - Background Checks: IC 16-27-2 (national or expanded, apply within 3 business days)
 *    - Drug Testing: HEA 1493 (effective July 2017) for direct patient contact
 *    - TB Screening: Required before client contact
 * 
 * Key Contacts:
 *    - IDOH Division of Home and Community-Based Care: (317) 233-7474
 *    - Home Health Program Manager: Kelly Hemmelgarn, khemmelgarn@isdh.in.gov, (317) 233-7541
 *    - PSA Program Email: PSA@health.in.gov
 *    - Indiana State Police (Background Checks): (317) 232-8248
 *    - Adult Protective Services: (800) 992-6978
 * 
 * Last Verified: December 2024
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const INDIANA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSE TYPE DETERMINATION & STATUS
  // ============================================
  {
    id: 'IN-A1',
    itemId: 'A1',
    stateCode: 'IN',
    title: 'Indiana License Type Determination',
    description: 'Indiana requires different licenses based on services provided',
    severity: 'high',
    regulationCode: '16-27-1',
    regulationName: 'License Type',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health/cshcr/acute-and-continuing-care/',
    questions: [
      {
        id: 'q1',
        question: 'What types of services does your agency provide?',
        inputType: 'select',
        options: [
          { value: 'hha', label: 'Skilled services (nursing, PT, OT, speech therapy, home health aide)' },
          { value: 'psa', label: 'Non-medical only (attendant care, homemaker, companion services)' },
          { value: 'both', label: 'Both skilled and non-medical services' },
        ],
        required: true,
        helpText: 'Your answer determines which Indiana license(s) you need. HHA = IC 16-27-1 / 410 IAC 17. PSA = IC 16-27-4.',
      },
    ],
    scoringRules: [
      { condition: 'exists', questionId: 'q1', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service type identified for license determination.',
      atRisk: 'Verify your services match your license type.',
      nonCompliant: 'Unable to determine appropriate license type.',
      unknown: 'Contact IDOH at (317) 233-7474 for license guidance.',
    },
    stateNotes: [
      'HHA License (skilled services): IC 16-27-1, 410 IAC 17',
      'PSA License (non-medical): IC 16-27-4',
      'Agencies providing both need BOTH licenses',
      'PSA agencies cannot use "HEALTH" or "NURSING" in their name per IC 16-27-1-15',
    ],
  },

  {
    id: 'IN-A2',
    itemId: 'A2',
    stateCode: 'IN',
    title: 'Home Health Agency License Status',
    description: 'HHA license required for skilled home health services per IC 16-27-1',
    severity: 'high',
    regulationCode: '16-27-1',
    regulationName: 'HHA Licensure',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health/cshcr/acute-and-continuing-care/home-health-agency-hha-licensing-and-certification-program/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide skilled services (nursing, therapy, home health aide)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - non-medical services only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a current Home Health Agency license from IDOH?',
        inputType: 'select',
        options: [
          { value: 'full', label: 'Yes - full license' },
          { value: 'provisional', label: 'Provisional license (pending survey)' },
          { value: 'pending', label: 'Application pending' },
          { value: 'no', label: 'No license' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 410 IAC 17-10-1, no HHA shall operate without a license from IDOH.',
      },
      {
        id: 'q3',
        question: 'When does your HHA license expire?',
        inputType: 'date',
        showCondition: {
          questionId: 'q2',
          operator: 'includes',
          value: ['full', 'provisional'],
        },
        helpText: 'HHA licenses are renewed annually. Submit renewal 60-90 days before expiration per 410 IAC 17-11-3.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'full', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'provisional', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['pending', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home Health Agency license is current and active.',
      atRisk: 'Provisional license - ensure survey readiness. Must have 3 active patients for survey per 410 IAC 17-10-1(e).',
      nonCompliant: 'Operating a HHA without a valid license is a Class A misdemeanor per IC 16-27-1-5. Contact IDOH immediately at (317) 233-7541.',
      unknown: 'Verify HHA license status with IDOH.',
    },
    stateNotes: [
      'License fee: $250 annually',
      'Renewal must be filed 60-90 days before expiration per 410 IAC 17-11-3',
      'Initial survey requires 3 active patients',
      'CHAP accreditation may substitute for state survey per 410 IAC 17-10-1(r)',
    ],
  },

  {
    id: 'IN-A3',
    itemId: 'A3',
    stateCode: 'IN',
    title: 'Personal Services Agency License Status',
    description: 'PSA license required for non-medical home care per IC 16-27-4',
    severity: 'high',
    regulationCode: '16-27-4',
    regulationName: 'PSA Licensure',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health/cshcr/acute-and-continuing-care/personal-care-services-agency-licensing-program/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide non-medical personal services (attendant care, homemaker, companion)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - skilled services only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a current Personal Services Agency license from IDOH?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - current license' },
          { value: 'pending', label: 'Application pending' },
          { value: 'no', label: 'No license' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per IC 16-27-4, operating without a PSA license is a Class A misdemeanor.',
      },
      {
        id: 'q3',
        question: 'When does your PSA license expire?',
        inputType: 'date',
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'PSA licenses are renewed annually.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personal Services Agency license is current.',
      atRisk: 'Application pending - do not provide services until license is issued.',
      nonCompliant: 'Operating a PSA without a license is a Class A misdemeanor per IC 16-27-4-23. Contact IDOH at PSA@health.in.gov.',
      unknown: 'Verify PSA license status with IDOH.',
    },
    stateNotes: [
      'License fee: $250 annually',
      'Must have physical location in Indiana',
      'Cannot use "HEALTH" or "NURSING" in agency name',
      'Submit policies in order shown on application page 4',
    ],
  },

  {
    id: 'IN-A4',
    itemId: 'A4',
    stateCode: 'IN',
    title: 'Administrator/Clinical Supervisor (HHA)',
    description: 'HHA administrator and clinical supervisor requirements per 410 IAC 17-12-1',
    severity: 'high',
    regulationCode: '17-12-1',
    regulationName: 'Administrator',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Does your HHA have the required leadership positions filled?',
        inputType: 'checklist',
        options: [
          { value: 'admin', label: 'Administrator with applicable Indiana license (if healthcare professional)' },
          { value: 'admin_alt', label: 'Alternate administrator designated' },
          { value: 'clinical', label: 'Clinical supervisor (Indiana-licensed RN)' },
          { value: 'clinical_alt', label: 'Alternate clinical supervisor designated' },
        ],
        required: true,
        helpText: 'Per 410 IAC 17-12-1, administrator and clinical supervisor credentials must be submitted with application.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator and clinical supervisor requirements met per 410 IAC 17-12-1.',
      atRisk: 'Designate alternates for both administrator and clinical supervisor positions.',
      nonCompliant: 'HHA must have qualified administrator and RN clinical supervisor. Submit credentials with license application.',
      unknown: 'Verify leadership qualifications.',
    },
    stateNotes: [
      'Administrator must have applicable Indiana license if healthcare professional per 410 IAC 17-12-1',
      'Clinical supervisor must be an Indiana-licensed RN',
      'Alternate administrator and clinical supervisor must be designated in writing',
      'Submit administrator and clinical supervisor credentials with license application',
    ],
  },

  {
    id: 'IN-A5',
    itemId: 'A5',
    stateCode: 'IN',
    title: 'PSA Manager Requirements',
    description: 'Personal Services Agency manager requirements per IC 16-27-4-9',
    severity: 'high',
    regulationCode: '16-27-4-9',
    regulationName: 'PSA Manager',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Does your PSA have a designated manager responsible for day-to-day operations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'IC 16-27-4-9(a) requires a manager for day-to-day operations.',
      },
      {
        id: 'q2',
        question: 'Do you have a written job description for the manager\'s responsibilities?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Submit job description of manager responsibilities with license application.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'PSA manager designation meets IC 16-27-4-9 requirements.',
      atRisk: 'Document manager responsibilities in writing per IC 16-27-4-9(a).',
      nonCompliant: 'PSA must have designated manager per IC 16-27-4-9.',
      unknown: 'Verify manager designation.',
    },
    stateNotes: [
      'IC 16-27-4-9(a) requires a manager responsible for day-to-day operations',
      'Written job description of manager responsibilities required with license application',
      'Manager does not need to be a licensed healthcare professional for PSA',
      'Contact PSA Program at PSA@health.in.gov for manager designation questions',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS (IC 16-27-2)
  // ============================================
  {
    id: 'IN-B1',
    itemId: 'B1',
    stateCode: 'IN',
    title: 'Criminal Background Check Requirements',
    description: 'IC 16-27-2 requires national or expanded criminal history checks',
    severity: 'high',
    regulationCode: '16-27-2',
    regulationName: 'Background Checks',
    agencyName: 'Indiana State Police',
    agencyAbbr: 'ISP',
    agencyPhone: '(317) 232-8248',
    agencyUrl: 'https://www.in.gov/isp/criminal-history-services/',
    questions: [
      {
        id: 'q1',
        question: 'Which type of criminal background check do you conduct for new employees?',
        inputType: 'select',
        options: [
          { value: 'national', label: 'National criminal history (FBI fingerprint via ISP INkless)' },
          { value: 'expanded', label: 'Expanded criminal history (FCRA-compliant consumer reporting agency)' },
          { value: 'state_only', label: 'Indiana state check only' },
          { value: 'none', label: 'No background checks' },
        ],
        required: true,
        helpText: 'IC 16-27-2 requires EITHER national (fingerprint) OR expanded (multi-state) checks. LIMITED criminal history is NO LONGER accepted as of July 1, 2016.',
      },
      {
        id: 'q2',
        question: 'When do you APPLY for the background check?',
        inputType: 'select',
        options: [
          { value: 'before_hire', label: 'Before hire/start date' },
          { value: 'within_3_days', label: 'Within 3 business days of starting' },
          { value: 'after_3_days', label: 'More than 3 days after starting' },
          { value: 'varies', label: 'Varies / no consistent timing' },
        ],
        required: true,
        helpText: 'IC 16-27-2-4: Must APPLY for check within 3 business days of when employee begins providing services.',
      },
      {
        id: 'q3',
        question: 'Do employees begin working with patients/clients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per IC 16-27-2, employees should not provide direct patient/client services until background clearance is received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before clearance - this is a DIRECT VIOLATION
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if using wrong type of background check
      { condition: 'equals', questionId: 'q1', value: 'state_only', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if applying too late
      { condition: 'includes', questionId: 'q2', value: ['after_3_days', 'varies'], result: 'NON_COMPLIANT' },
      // COMPLIANT only if proper check type, timely application, AND no early starts
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['national', 'expanded'] },
        { condition: 'includes', questionId: 'q2', value: ['before_hire', 'within_3_days'] },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check procedures meet IC 16-27-2 requirements.',
      atRisk: 'Verify background check type and timing comply with IC 16-27-2.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance is a direct violation of IC 16-27-2. Per regulations, NO patient/client contact is allowed until clearance received. "Limited criminal history" checks are NO LONGER accepted as of July 1, 2016 per Senate Enrolled Act 350. Must use national (fingerprint) or expanded (FCRA) checks.',
      unknown: 'Review IC 16-27-2 requirements for criminal history checks.',
    },
    stateNotes: [
      'IMPORTANT: "Limited criminal history" NO LONGER accepted per Senate Enrolled Act 350 (2016)',
      'National check: FBI fingerprint via ISP INkless program',
      'Expanded check: FCRA-compliant multi-state search (no credit info)',
      'Must APPLY within 3 business days of employee starting services',
      'IC 16-27-2-5 lists disqualifying convictions',
    ],
  },

  {
    id: 'IN-B2',
    itemId: 'B2',
    stateCode: 'IN',
    title: 'Sex Offender Registry Verification',
    description: 'Sex offender registry check required with national background checks',
    severity: 'high',
    regulationCode: '11-8-8',
    regulationName: 'Sex Offender Registry',
    agencyName: 'Indiana State Police',
    agencyAbbr: 'ISP',
    agencyPhone: '(317) 232-8248',
    agencyUrl: 'https://www.in.gov/isp/sex-and-violent-offender-registry/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check sex offender registries for all new employees?',
        inputType: 'select',
        options: [
          { value: 'all_50', label: 'Yes - all 50 states or national DOJ registry' },
          { value: 'indiana_only', label: 'Indiana registry only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'If using national (fingerprint) background checks, must also check sex offender registries in all 50 states OR the national DOJ registry.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all_50', result: 'COMPLIANT' },
      { condition: 'equals', value: 'indiana_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Sex offender registry verification meets Indiana requirements.',
      atRisk: 'If using national fingerprint checks, must verify all 50 state registries or national DOJ registry.',
      nonCompliant: 'Sex offender registry checks are required. Free search at in.gov/isp.',
      unknown: 'Verify sex offender registry check procedures.',
    },
    stateNotes: [
      'Indiana Sex Offender Registry searchable at in.gov/isp/sex-and-violent-offender-registry',
      'If using national fingerprint background check, must also check national DOJ sex offender registry',
      'IC 11-8-8 governs Sex and Violent Offender Registration in Indiana',
      'Free registry search available - no cost to employers',
    ],
  },

  {
    id: 'IN-B3',
    itemId: 'B3',
    stateCode: 'IN',
    title: 'Nurse Aide Registry and OIG Exclusion Checks',
    description: 'Registry and exclusion list verification requirements',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusions',
    agencyName: 'Indiana Professional Licensing Agency / OIG',
    agencyAbbr: 'PLA/OIG',
    agencyPhone: '(317) 234-2043',
    agencyUrl: 'https://www.in.gov/pla/',
    questions: [
      {
        id: 'q1',
        question: 'Which registry/exclusion checks do you conduct for all new hires?',
        inputType: 'checklist',
        options: [
          { value: 'nurse_aide', label: 'Indiana Nurse Aide Registry' },
          { value: 'oig', label: 'OIG LEIE (List of Excluded Individuals/Entities)' },
          { value: 'sam', label: 'SAM.gov exclusion list' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you re-check OIG/SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'never', label: 'Never' },
        ],
        helpText: 'OIG recommends monthly checks for Medicare/Medicaid providers.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'monthly' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Registry and exclusion verification meets requirements.',
      atRisk: 'Conduct all three checks (Nurse Aide Registry, OIG LEIE, SAM.gov) and verify monthly.',
      nonCompliant: 'Implement comprehensive registry/exclusion verification before hiring.',
      unknown: 'Review registry check requirements.',
    },
    stateNotes: [
      'Indiana Nurse Aide Registry: verify through Indiana Professional Licensing Agency (PLA)',
      'OIG LEIE: exclusions.oig.hhs.gov - check monthly for Medicare/Medicaid providers',
      'SAM.gov: federal exclusion list for government contractors and healthcare providers',
      'Hiring excluded individuals results in federal civil monetary penalties',
      'Indiana PLA online verification: in.gov/pla',
    ],
  },

  // ============================================
  // SECTION C: DRUG TESTING (HEA 1493)
  // ============================================
  {
    id: 'IN-C1',
    itemId: 'C1',
    stateCode: 'IN',
    title: 'Drug Testing Requirements',
    description: 'HEA 1493 (2017) requires drug testing for employees with direct patient contact',
    severity: 'high',
    regulationCode: '16-27-1-20',
    regulationName: 'Drug Testing',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct drug testing for employees with direct patient/client contact?',
        inputType: 'select',
        options: [
          { value: 'pre_and_ongoing', label: 'Yes - pre-employment and reasonable suspicion/random' },
          { value: 'pre_only', label: 'Yes - pre-employment only' },
          { value: 'no', label: 'No drug testing program' },
        ],
        required: true,
        helpText: 'Effective July 1, 2017, HEA 1493 requires drug testing for employees having direct contact with patients/clients.',
      },
      {
        id: 'q2',
        question: 'Do you have a written drug testing policy?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['pre_and_ongoing', 'pre_only'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Drug testing program meets HEA 1493 requirements.',
      atRisk: 'Document drug testing policy in writing.',
      nonCompliant: 'CRITICAL: Drug testing required since July 1, 2017 per HEA 1493 (IC 16-27-1-20 for HHA, IC 16-27-4-24 for PSA) for employees with direct patient/client contact.',
      unknown: 'Review HEA 1493 drug testing requirements.',
    },
    stateNotes: [
      'Effective July 1, 2017 per House Enrolled Act 1493',
      'Applies to BOTH HHA and PSA agencies',
      'Testing for illegal use of controlled substances',
      'IDOH Program Advisory Letter provides implementation guidance',
    ],
  },

  // ============================================
  // SECTION D: HEALTH SCREENING
  // ============================================
  {
    id: 'IN-D1',
    itemId: 'D1',
    stateCode: 'IN',
    title: 'TB Screening Requirements',
    description: 'TB screening required before direct patient/client contact',
    severity: 'high',
    regulationCode: '17-12-1(i)',
    regulationName: 'TB Screening',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees with direct patient/client contact have TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all have current TB clearance' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is TB screening completed BEFORE any patient/client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before contact' },
          { value: 'no', label: 'No - sometimes after starting' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening procedures meet Indiana requirements.',
      atRisk: 'TB screening must be completed BEFORE patient/client contact.',
      nonCompliant: 'TB screening required for all direct care workers before patient contact.',
      unknown: 'Review TB screening requirements.',
    },
    stateNotes: [
      'HHA: 410 IAC 17-12-1(i) - TB screening required',
      'PSA: IC 16-27-4-15 - control of communicable disease',
      'IDOH Advisory AC-2019-01-HHA allows nationally recognized standards as alternative',
    ],
  },

  {
    id: 'IN-D2',
    itemId: 'D2',
    stateCode: 'IN',
    title: 'Physical Examination (HHA)',
    description: 'Physical exam required within 180 days before direct patient contact',
    severity: 'high',
    regulationCode: '17-12-1(h)',
    regulationName: 'Physical Exam',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL HHA employees with direct patient contact have physical exam documentation?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all have physicals within 180 days before patient contact' },
          { value: 'most', label: 'Most do' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 410 IAC 17-12-1(h), physical exam by physician or NP required within 180 days BEFORE direct patient contact.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Physical examination documentation meets 410 IAC 17-12-1(h) requirements.',
      atRisk: 'Some employees may be missing physical exam documentation.',
      nonCompliant: 'Physical exam required within 180 days BEFORE direct patient contact per 410 IAC 17-12-1(h).',
      unknown: 'Verify physical exam documentation.',
    },
    stateNotes: [
      'Physical exam required within 180 days BEFORE direct patient contact per 410 IAC 17-12-1(h)',
      'Exam must be performed by physician or nurse practitioner',
      'Documentation must be maintained in personnel file',
      'Applies to HHA employees only - separate from TB screening requirement',
    ],
  },

  // ============================================
  // SECTION E: TRAINING REQUIREMENTS
  // ============================================
  {
    id: 'IN-E1',
    itemId: 'E1',
    stateCode: 'IN',
    title: 'Home Health Aide Requirements (HHA)',
    description: 'HHA training and registry requirements per 410 IAC 17-14-1',
    severity: 'high',
    regulationCode: '17-14-1',
    regulationName: 'HHA Training',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7351',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides meet Indiana requirements?',
        inputType: 'checklist',
        options: [
          { value: 'competency', label: 'Completed competency evaluation program' },
          { value: 'registry', label: 'Listed on Indiana Home Health Aide Registry in good standing' },
          { value: 'documented', label: 'Training documented in personnel file' },
          { value: 'supervised', label: 'Supervised by health care professional per 410 IAC 17-14-1(g)' },
        ],
        required: true,
        helpText: 'Per 410 IAC 17-14-1, HHAs must complete competency evaluation and be on state registry.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide requirements met per 410 IAC 17-14-1.',
      atRisk: 'Some HHA requirements may be incomplete. Verify registry status and supervision.',
      nonCompliant: 'HHAs must complete competency evaluation and be on state registry per 410 IAC 17-14-1.',
      unknown: 'Verify HHA training and registry status.',
    },
    stateNotes: [
      'HHA Registry: (317) 233-7351, Aide Registry Manager: Darlene Jones dkjones@isdh.in.gov',
      'Register through Indiana Professional Licensing Agency (PLA) online system',
      'Must pass competency evaluation covering subjects in state rule',
      'Medicare-certified HHAs must also meet 42 CFR 484.36',
    ],
  },

  {
    id: 'IN-E2',
    itemId: 'E2',
    stateCode: 'IN',
    title: 'Annual In-Service Training (HHA)',
    description: 'Annual continuing education requirements per 410 IAC 17-14-1(h)',
    severity: 'high',
    regulationCode: '17-14-1(h)',
    regulationName: 'Annual Training',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7351',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do your home health aides complete required annual in-service training?',
        inputType: 'select',
        options: [
          { value: '12_hours', label: 'Yes - 12 hours per 12-month period' },
          { value: 'some', label: 'Some training, but less than 12 hours' },
          { value: 'no', label: 'No annual training program' },
        ],
        required: true,
        helpText: 'Per 410 IAC 17-14-1(h): 12 hours in-service per 12-month period. First-year aides receive prorated hours per (i).',
      },
      {
        id: 'q2',
        question: 'Is training conducted under RN supervision?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - under general supervision of RN per (k)' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 410 IAC 17-14-1(k), training must be performed by or under general supervision of RN.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '12_hours' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets 410 IAC 17-14-1(h) requirements.',
      atRisk: 'Must provide 12 hours annual in-service under RN supervision.',
      nonCompliant: 'Implement 12-hour annual in-service training program per 410 IAC 17-14-1(h).',
      unknown: 'Verify annual training program.',
    },
    stateNotes: [
      '12 hours per 12-month period per 410 IAC 17-14-1(h)',
      'First-year aides: prorated hours per (i)',
      'Training under general supervision of RN per (k)',
      'Agency with probationary license cannot offer continuing education per (j)',
    ],
  },

  {
    id: 'IN-E3',
    itemId: 'E3',
    stateCode: 'IN',
    title: 'PSA Competency Evaluation',
    description: 'PSA employee competency evaluation per IC 16-27-4-16',
    severity: 'high',
    regulationCode: '16-27-4-16',
    regulationName: 'Competency',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do you evaluate employee competency for EACH attendant care task before unsupervised work?',
        inputType: 'select',
        options: [
          { value: 'per_task', label: 'Yes - evaluated per task before unsupervised work' },
          { value: 'general', label: 'General evaluation only, not per task' },
          { value: 'no', label: 'No formal competency evaluation' },
        ],
        required: true,
        helpText: 'IC 16-27-4-16(a): Competency must be evaluated for EACH attendant care task. Cannot perform task unsupervised until deemed competent.',
      },
      {
        id: 'q2',
        question: 'Do you document evaluations with date and signature of evaluator?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'IC 16-27-4-16(d) requires documentation including date and signature.',
      },
      {
        id: 'q3',
        question: 'Do you train and re-evaluate on tasks needing improvement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'IC 16-27-4-16(b) requires training on tasks needing improvement, then re-evaluation.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'per_task' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'general', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PSA competency evaluation meets IC 16-27-4-16 requirements.',
      atRisk: 'Competency must be evaluated per task, not just generally. Document with date and evaluator signature.',
      nonCompliant: 'Implement per-task competency evaluation before unsupervised work per IC 16-27-4-16.',
      unknown: 'Review IC 16-27-4-16 competency requirements.',
    },
    stateNotes: [
      'Evaluation required for EACH attendant care task',
      'Agency has sole discretion to determine competency',
      'Train and re-evaluate on tasks needing improvement',
      'Document: content, date, signature of evaluator',
      'IC 16-27-4-16(c): Lift services require additional training',
    ],
  },

  {
    id: 'IN-E4',
    itemId: 'E4',
    stateCode: 'IN',
    title: 'Abuse Recognition and Reporting Training',
    description: 'Staff training on abuse recognition and mandatory reporting',
    severity: 'high',
    regulationCode: '12-10-3',
    regulationName: 'Abuse Reporting',
    agencyName: 'Indiana Family and Social Services Administration',
    agencyAbbr: 'FSSA',
    agencyPhone: '(800) 992-6978',
    agencyUrl: 'https://www.in.gov/fssa/da/adult-protective-services/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive training on abuse, neglect, and exploitation recognition and reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all employees trained' },
          { value: 'some', label: 'Some employees trained' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do employees know how to report suspected abuse to Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - reporting procedure documented and trained' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse recognition and reporting training meets requirements.',
      atRisk: 'All employees must be trained. Document APS reporting procedure: (800) 992-6978.',
      nonCompliant: 'Implement abuse recognition training for all employees.',
      unknown: 'Review abuse training requirements.',
    },
    stateNotes: [
      'Indiana Adult Protective Services hotline: (800) 992-6978',
      'IC 12-10-3 governs adult protective services and mandatory reporting',
      'All home health workers should know signs of abuse, neglect, and exploitation',
      'Reports may be made anonymously - good faith reporters are protected',
      'Failure to report suspected abuse may result in disciplinary action',
    ],
  },

  // ============================================
  // SECTION F: PERSONNEL FILES
  // ============================================
  {
    id: 'IN-F1',
    itemId: 'F1',
    stateCode: 'IN',
    title: 'Personnel File Documentation (HHA)',
    description: 'Required personnel file contents per 410 IAC 17-12-1(f)(g)',
    severity: 'high',
    regulationCode: '17-12-1(f)',
    regulationName: 'Personnel Files',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY HHA employee\'s personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'Documentation of orientation to the job' },
          { value: 'background', label: 'National or expanded criminal history check (not limited)' },
          { value: 'license', label: 'Copy of current license, certification, or registration' },
          { value: 'physical', label: 'Physical exam (within 180 days before patient contact)' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'performance', label: 'Performance evaluations (every 9-15 months)' },
          { value: 'training', label: 'Training and competency documentation' },
          { value: 'drug_test', label: 'Drug testing results (per HEA 1493)' },
        ],
        required: true,
        helpText: 'Per 410 IAC 17-12-1(f), personnel records must include orientation, background check, and current credentials.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all required documentation per 410 IAC 17-12-1.',
      atRisk: 'Some personnel files may be missing required documents.',
      nonCompliant: 'Personnel files are missing critical documentation. IDOH surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against 410 IAC 17-12-1 requirements.',
    },
    stateNotes: [
      'Performance evaluations: every 9-15 months per 410 IAC 17-12-1(g)',
      'Physical exam: within 180 days BEFORE direct patient contact per (h)',
      'Background check: national or expanded only - limited NOT accepted since 2016',
    ],
  },

  // ============================================
  // SECTION G: RECORD RETENTION
  // ============================================
  {
    id: 'IN-G1',
    itemId: 'G1',
    stateCode: 'IN',
    title: 'Record Retention - PSA',
    description: 'PSA record retention requirements per IC 16-27-4-18',
    severity: 'medium',
    regulationCode: '16-27-4-18',
    regulationName: 'Record Retention',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain PSA client and compliance documentation?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '5_6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'no_policy', label: 'No formal retention policy' },
        ],
        required: true,
        helpText: 'IC 16-27-4-18: Documentation must be maintained for not less than 7 years.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5_6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'no_policy'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets IC 16-27-4-18 requirement of 7 years.',
      atRisk: 'PSA records must be retained for at least 7 years per IC 16-27-4-18.',
      nonCompliant: 'Implement 7-year record retention policy per IC 16-27-4-18.',
      unknown: 'Review PSA record retention requirements.',
    },
    stateNotes: [
      'IC 16-27-4-18: Documentation retained for not less than 7 years',
      'Must be maintained or electronically accessible at Indiana office',
      'Includes compliance documentation AND services provided to clients',
    ],
  },

  // ============================================
  // SECTION H: CARE DELIVERY
  // ============================================
  {
    id: 'IN-H1',
    itemId: 'H1',
    stateCode: 'IN',
    title: 'Plan of Care (HHA)',
    description: 'Medical and nursing plan of care requirements per 410 IAC 17-13',
    severity: 'high',
    regulationCode: '17-13-1',
    regulationName: 'Plan of Care',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written plans of care for ALL patients before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do plans of care include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Diagnosis and prognosis' },
          { value: 'orders', label: 'Physician orders' },
          { value: 'services', label: 'Services to be provided' },
          { value: 'frequency', label: 'Frequency and duration' },
          { value: 'goals', label: 'Patient goals' },
          { value: 'signature', label: 'Physician signature' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care process meets 410 IAC 17-13 requirements.',
      atRisk: 'Plans of care must be completed BEFORE services begin.',
      nonCompliant: 'Implement plan of care process per 410 IAC 17-13.',
      unknown: 'Review plan of care requirements.',
    },
    stateNotes: [
      '410 IAC 17-13 requires written plan of care for all HHA patients',
      'Plan must be signed by attending physician before services begin',
      'Must include diagnosis, prognosis, services, frequency, and patient goals',
      'Review and update at least every 60 days per Medicare CoPs (42 CFR 484)',
    ],
  },

  {
    id: 'IN-H2',
    itemId: 'H2',
    stateCode: 'IN',
    title: 'Service Plan (PSA)',
    description: 'PSA service plan requirements per IC 16-27-4',
    severity: 'high',
    regulationCode: '16-27-4-12',
    regulationName: 'Service Plan',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7474',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written service plans for ALL PSA clients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all clients have service plans' },
          { value: 'most', label: 'Most clients' },
          { value: 'no', label: 'No formal service plans' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service plan documentation meets requirements.',
      atRisk: 'All clients must have service plans.',
      nonCompliant: 'Implement service plan process.',
      unknown: 'Review service plan requirements.',
    },
    stateNotes: [
      'IC 16-27-4 requires written service plan for all PSA clients',
      'Service plan should specify tasks to be performed and frequency',
      'Client or responsible party should sign acknowledging the plan',
      'Update service plan when client needs change',
    ],
  },

  // ============================================
  // SECTION I: PATIENT/CLIENT RIGHTS
  // ============================================
  {
    id: 'IN-I1',
    itemId: 'I1',
    stateCode: 'IN',
    title: 'Patient Rights (HHA)',
    description: 'Patient rights requirements per 410 IAC 17-12-3',
    severity: 'high',
    regulationCode: '17-12-3',
    regulationName: 'Patient Rights',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights at admission and obtain acknowledgment?',
        inputType: 'select',
        options: [
          { value: 'yes_documented', label: 'Yes - provided and signed acknowledgment in file' },
          { value: 'yes_no_signature', label: 'Provided but no signed acknowledgment' },
          { value: 'no', label: 'Not consistently provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you inform patients how to file complaints with IDOH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes_documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes_no_signature', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights process meets 410 IAC 17-12-3 requirements.',
      atRisk: 'Obtain signed acknowledgment and inform patients of IDOH complaint process.',
      nonCompliant: 'Patient rights must be provided at admission per 410 IAC 17-12-3.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      '410 IAC 17-12-3 requires written patient rights at admission',
      'Patient or legal representative must acknowledge receipt in writing',
      'Must include information on how to file complaints with IDOH',
      'IDOH complaint hotline: (317) 233-7474',
      'Rights must be provided in language patient understands',
    ],
  },

  // ============================================
  // SECTION J: QUALITY & COMPLIANCE
  // ============================================
  {
    id: 'IN-J1',
    itemId: 'J1',
    stateCode: 'IN',
    title: 'QAPI Program (HHA)',
    description: 'Quality Assessment and Performance Improvement per 410 IAC 17-12-2',
    severity: 'medium',
    regulationCode: '17-12-2',
    regulationName: 'QAPI',
    agencyName: 'Indiana Department of Health',
    agencyAbbr: 'IDOH',
    agencyPhone: '(317) 233-7541',
    agencyUrl: 'https://www.in.gov/health',
    questions: [
      {
        id: 'q1',
        question: 'Does your HHA have a documented QAPI program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which QAPI activities does your program include?',
        inputType: 'checklist',
        options: [
          { value: 'outcomes', label: 'Patient outcome monitoring' },
          { value: 'incidents', label: 'Incident tracking and analysis' },
          { value: 'satisfaction', label: 'Patient satisfaction surveys' },
          { value: 'improvement', label: 'Performance improvement initiatives' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 410 IAC 17-12-2 requirements.',
      atRisk: 'Expand QAPI program to include additional quality activities.',
      nonCompliant: 'Implement QAPI program per 410 IAC 17-12-2.',
      unknown: 'Review QAPI requirements.',
    },
    stateNotes: [
      '410 IAC 17-12-2 requires ongoing quality assessment and performance improvement',
      'QAPI must include patient outcome monitoring and incident tracking',
      'Medicare-certified HHAs must also meet 42 CFR 484.65 QAPI requirements',
      'Document performance improvement initiatives and results',
      'Governing body oversight of QAPI required',
    ],
  },

  {
    id: 'IN-J2',
    itemId: 'J2',
    stateCode: 'IN',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid personal care services',
    severity: 'high',
    regulationCode: 'Cures Act',
    regulationName: 'EVV',
    agencyName: 'Indiana Family and Social Services Administration',
    agencyAbbr: 'FSSA',
    agencyPhone: '(800) 457-4584',
    agencyUrl: 'https://www.in.gov/fssa/ompp/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use an Indiana-approved EVV system for ALL Medicaid visits?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - fully implemented' },
          { value: 'partial', label: 'Partially implemented' },
          { value: 'no', label: 'No EVV system' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV requirements are met or not applicable.',
      atRisk: 'EVV must be used for ALL Medicaid personal care visits.',
      nonCompliant: 'Federal law requires EVV for Medicaid personal care services. Contact FSSA at (800) 457-4584.',
      unknown: 'Determine EVV requirements for your services.',
    },
    stateNotes: [
      'Indiana uses Sandata as the state EVV aggregator',
      '21st Century Cures Act mandates EVV for Medicaid personal care services',
      'EVV must capture: date, time in/out, location, services, provider identity',
      'Contact OMPP for EVV enrollment and technical requirements',
      'Non-compliance may result in claim denials',
    ],
  },
];

export default INDIANA_QUESTIONS;